import { Component, OnInit, ViewEncapsulation, ChangeDetectorRef, DestroyRef, OnDestroy } from '@angular/core';
import { ActivatedRoute, Data } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { Observable, noop } from 'rxjs';
import { AccountService } from 'app/core';
import {
  ProjectType,
  PropertyType,
  ProjectStage,
  InvestmentType,
  Team,
  Vehicle,
  MarketStatus,
  LoanPurpose,
  ProjectPhase,
  CallOutcome,
  MeetingOutcome,
  EngagementActivityType,
  SocialMediaSource,
} from 'app/core/models/account.types';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { Pipeline, ProjectTemplate } from 'app/modules/projects/projects.types';
import { Category } from 'app/modules/contacts/contacts.types';
import { Select, Store } from '@ngxs/store';
import { ObjectsManagerState } from '../objects-manager/state/state';
import { CustomObject } from 'app/shared/modules/custom-objects/custom-objects.types';
import { ObjectsManagerActions } from '../objects-manager/state/actions';
import { FeatureFlagService } from 'app/core/services/feature-flag.service';
import { ReorderService } from '@services/reorder.service';
import { AccountState } from 'app/state/account/state';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'customizations-settings',
  templateUrl: './customizations.component.html',
  styleUrls: ['./customizations.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CustomizationsSettingsComponent implements OnInit, OnDestroy {
  public dropdowns: any;
  private dropdownsChanges = false;

  public investmentTypes: InvestmentType[];
  public marketStatuses: MarketStatus[];
  public projectTypes: ProjectType[];
  public propertyTypes: PropertyType[];
  public projectStages: ProjectStage[];
  public projectTemplates: ProjectTemplate<any>[];
  public projectPhases: ProjectPhase[];
  public teams: Team[];
  public vehicles: Vehicle[];
  public pipelines: Pipeline[];
  public categories: Category[];
  public loanPurposes: LoanPurpose[];
  public customObjectLabelMap: Map<string, string> = new Map<string, string>();
  public callOutcomes: Vehicle[];
  public meetingOutcomes: Vehicle[];
  public engagementActivityTypes: Vehicle[];
  public socialMediaSources: SocialMediaSource[];

  @Select(ObjectsManagerState.get) customObjects$: Observable<CustomObject[]>;

  /**
   * Constructor
   *
   * @param {FormBuilder} _formBuilder
   */
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _changeDetectorRef: ChangeDetectorRef,
    private _accountService: AccountService,
    private _projectsService: ProjectsService,
    private readonly store: Store,
    private readonly reorderService: ReorderService,
    private readonly destroyRef: DestroyRef,
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */

  ngOnInit(): void {
    this.customObjects$
      .pipe(
        filter((response) => !!response.length),
        take(1),
      )
      .subscribe((customObjects) => {
        customObjects.forEach((customObject) => {
          this.customObjectLabelMap.set(customObject.name, customObject.label);
        });
        this._changeDetectorRef.markForCheck();
      });

    this.store.dispatch(new ObjectsManagerActions.Get());

    this._activatedRoute.data.subscribe((data: Data) => {
      this.dropdowns = data.options;
      this.projectTemplates = data.options.project_templates.sort((a, b) => a.rank - b.rank);
    });

    this._accountService.investmentTypes$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((investmentTypes: InvestmentType[]) => {
        this.investmentTypes = investmentTypes;
        this._changeDetectorRef.markForCheck();
      });

    this._accountService.projectTypes$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((projectTypes: ProjectType[]) => {
        this.projectTypes = projectTypes;
        this._changeDetectorRef.markForCheck();
      });

    this._accountService.propertyTypes$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((propertyTypes: PropertyType[]) => {
        this.propertyTypes = propertyTypes;
        this._changeDetectorRef.markForCheck();
      });

    this._accountService.projectStages$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((projectStages: ProjectStage[]) => {
        this.projectStages = projectStages;
        this._changeDetectorRef.markForCheck();
      });

    this._accountService.projectPhases$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((projectPhases: ProjectPhase[]) => {
        this.projectPhases = projectPhases;
        this._changeDetectorRef.markForCheck();
      });

    this._accountService.teams$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((teams: Team[]) => {
      this.teams = teams;
      this._changeDetectorRef.markForCheck();
    });

    this._accountService.vehicles$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((vehicles: Vehicle[]) => {
      this.vehicles = vehicles;
      this._changeDetectorRef.markForCheck();
    });

    this._accountService.marketStatuses$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((marketStatuses: MarketStatus[]) => {
        this.marketStatuses = marketStatuses;
        this._changeDetectorRef.markForCheck();
      });

    this._accountService.categories$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((categories: Category[]) => {
      this.categories = categories;
      this._changeDetectorRef.markForCheck();
    });

    this._accountService.loanPurposes$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loanPurposes: LoanPurpose[]) => {
        this.loanPurposes = loanPurposes;
        this._changeDetectorRef.markForCheck();
      });

    this._projectsService.pipelines$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((pipelines) => {
      if (pipelines) {
        // Store the calendars
        this.pipelines = pipelines.filter((pipeline) => pipeline?.pipeline_type != 'dynamic');
        this._changeDetectorRef.markForCheck();
      }
    });

    this._accountService.callOutcomes$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((callOutcomes: CallOutcome[]) => {
        this.callOutcomes = callOutcomes;
        this._changeDetectorRef.markForCheck();
      });

    this._accountService.meetingOutcomes$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((meetingOutcomes: MeetingOutcome[]) => {
        this.meetingOutcomes = meetingOutcomes;
        this._changeDetectorRef.markForCheck();
      });

    this._accountService.engagementActivityTypes$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((engagementActivityTypes: EngagementActivityType[]) => {
        this.engagementActivityTypes = engagementActivityTypes;
        this._changeDetectorRef.markForCheck();
      });

    this._accountService.socialMediaSources$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((socialMediaSources: SocialMediaSource[]) => {
        this.socialMediaSources = socialMediaSources;
        this._changeDetectorRef.markForCheck();
      });
  }

  ngOnDestroy(): void {
    if (this.dropdownsChanges) {
      this._accountService.bustStandardDropdownsCache();
      this._accountService.getStandardDropdowns().subscribe(noop);
    }
  }

  updatePropertyType($event): void {
    this._accountService.updatePropertyType($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  addPropertyType($event): void {
    this._accountService.createPropertyType($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  updateProjectPhase($event): void {
    this._accountService.updateProjectPhase($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  addProjectPhase($event): void {
    this._accountService.createProjectPhase($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  updateProjectStage($event): void {
    this._accountService.updateProjectStage($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  addProjectStage($event): void {
    this._accountService.createProjectStage($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  updateProjectType($event): void {
    this._accountService.updateProjectType($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  addProjectType($event): void {
    this._accountService.createProjectType($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  updateInvestmentType($event): void {
    this._accountService.updateInvestmentType($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  addInvestmentType($event): void {
    this._accountService.createInvestmentType($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  addCategory($event): void {
    this._accountService.createCategory($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  updateCategory($event): void {
    this._accountService.updateCategory($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  updateMarketStatus($event): void {
    this._accountService.updateMarketStatus($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  addMarketStatus($event): void {
    this._accountService.createMarketStatus($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  updateVehicle($event): void {
    this._accountService.updateVehicle($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  addVehicle($event): void {
    this._accountService.createVehicle($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  updateTeam($event): void {
    this._accountService.updateTeam($event).subscribe();
  }

  addTeam($event): void {
    this._accountService.createTeam($event).subscribe();
  }

  updateLoanPurpose($event): void {
    this._accountService.updateLoanPurpose($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  addLoanPurpose($event): void {
    this._accountService.createLoanPurpose($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  updateProjectTemplate($event): void {
    this._projectsService.updateProjectTemplate($event).subscribe();
  }

  addProjectTemplate($event): void {
    this._accountService.createProjectType($event).subscribe();
  }

  updateCallOutcome($event): void {
    this._accountService.updateCallOutcome($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  addCallOutcome($event): void {
    this._accountService.createCallOutcome($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  updateMeetingOutcome($event): void {
    this._accountService.updateMeetingOutcome($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  addMeetingOutcome($event): void {
    this._accountService.createMeetingOutcome($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  updateEngagementActivityType($event): void {
    this._accountService.updateEngagementActivityType($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  addEngagementActivityType($event): void {
    this._accountService.createEngagementActivityType($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  updateSocialMediaSource($event): void {
    this._accountService.updateSocialMediaSources($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  addSocialMediaSource($event): void {
    this._accountService.createSocialMediaSources($event).subscribe(() => this.shouldRefreshStandardDropdowns());
  }

  shouldRefreshStandardDropdowns(): void {
    this.dropdownsChanges = true;
  }

  onRankChanged(items: any[], type: string): void {
    const account = this.store.selectSnapshot(AccountState.getAccount);
    this.reorderService
      .update(
        +account.id,
        type,
        items.reduce((p, c, i) => ({ ...p, [c.id]: i }), {}),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(noop);
  }
}
